export const normalizeText = (text: string | undefined): string => {
  if (!text) return '';
  return text.replace(/\s+/g, ' ').replace(/[“”]/g, '"').replace(/[‘’]/g, "'").trim();
};

export interface TextQuote {
  highlight: string;
  before?: string;
  after?: string;
}

export interface TextOffsetRange {
  start: number;
  end: number;
}

function commonSuffixLength(a: string, b: string): number {
  let length = 0;
  while (length < a.length && length < b.length && a.at(-1 - length) === b.at(-1 - length)) {
    length += 1;
  }
  return length;
}

function commonPrefixLength(a: string, b: string): number {
  let length = 0;
  while (length < a.length && length < b.length && a[length] === b[length]) {
    length += 1;
  }
  return length;
}

// Extra chars to slice so whitespace-run normalization doesn't leave the window too short.
const CONTEXT_WINDOW_SLACK = 20;

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ');
}

function contextScore(text: string, range: TextOffsetRange, quote: TextQuote): number {
  const { before, after } = quote;
  let score = 0;
  if (before) {
    const window = text.slice(
      Math.max(0, range.start - before.length - CONTEXT_WINDOW_SLACK),
      range.start,
    );
    // `before` never includes the separator right before the highlight, but `window` always does.
    const preceding = normalizeWhitespace(window).replace(/ $/, '');
    score += commonSuffixLength(preceding, normalizeWhitespace(before));
  }
  if (after) {
    const window = text.slice(range.end, range.end + after.length + CONTEXT_WINDOW_SLACK);
    const following = normalizeWhitespace(window).replace(/^ /, '');
    score += commonPrefixLength(following, normalizeWhitespace(after));
  }
  return score;
}

// Disambiguates repeated occurrences of `quote.highlight` in `text` using before/after context.
export function findQuoteOccurrence(text: string, quote: TextQuote): TextOffsetRange | undefined {
  const { highlight } = quote;
  if (!highlight) return undefined;

  let searchFrom = 0;
  let best: TextOffsetRange | undefined = undefined;
  let bestScore = -1;
  for (;;) {
    const start = text.indexOf(highlight, searchFrom);
    if (start === -1) break;
    const range = { start, end: start + highlight.length };
    const score = contextScore(text, range, quote);
    if (score > bestScore) {
      bestScore = score;
      best = range;
    }
    searchFrom = start + 1;
  }

  return best;
}

export interface CollapsedText {
  text: string;
  /** `rawOffsets[i]` is the offset in the original text of `text[i]`; one extra entry maps `text.length`. */
  rawOffsets: number[];
}

const WHITESPACE = /\s/;

// Collapses every whitespace run into one space, remembering where each kept character came from,
// so a quote captured from rendered text (single spaces) can be found in pretty-printed XHTML text
// (newlines plus indentation) and mapped back to offsets in the original text.
export function collapseWhitespace(raw: string): CollapsedText {
  let text = '';
  const rawOffsets: number[] = [];
  // Tracks the previous character instead of `text.endsWith(' ')`, which makes V8 flatten the
  // growing string on every step and turns this quadratic on long chapters.
  let previousWasSpace = false;
  for (let index = 0; index < raw.length; index += 1) {
    const char = raw.charAt(index);
    const isSpace = WHITESPACE.test(char);
    if (!isSpace || !previousWasSpace) {
      text += isSpace ? ' ' : char;
      rawOffsets.push(index);
    }
    previousWasSpace = isSpace;
  }
  rawOffsets.push(raw.length);
  return { text, rawOffsets };
}

// Drops every whitespace character, remembering where each kept one came from: quotes and chapter
// text are matched without whitespace so it does not matter where each source puts spaces (a
// selection across <i>…</i>, a footnote call or a drop cap, pretty-printed XHTML).
export function removeWhitespace(raw: string): CollapsedText {
  let text = '';
  const rawOffsets: number[] = [];
  for (let index = 0; index < raw.length; index += 1) {
    const char = raw.charAt(index);
    if (WHITESPACE.test(char)) continue;
    text += char;
    rawOffsets.push(index);
  }
  rawOffsets.push(raw.length);
  return { text, rawOffsets };
}
